import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';

export interface KnowledgePaginationOptions {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}

export interface KnowledgePaginationState {
	currentPage: number;
	pageSize: number;
	sortBy: string;
	sortOrder: 'asc' | 'desc';
}

export interface UseKnowledgeQueryOptions {
	selectedId?: string;
	pagination?: KnowledgePaginationState;
	enabled?: boolean;
}

/**
 * Custom hook for fetching paginated knowledge data
 * @param options - Query options including pagination state
 * @returns React Query result with knowledge data and pagination info
 */
export const useKnowledgeQuery = (options: UseKnowledgeQueryOptions = {}) => {
	const {
		selectedId,
		pagination = {
			currentPage: 1,
			pageSize: 10,
			sortBy: 'createdAt',
			sortOrder: 'desc',
		},
		enabled = true,
	} = options;

	return useQuery({
		queryKey: [
			'knowledge',
			selectedId,
			pagination.currentPage,
			pagination.pageSize,
			pagination.sortBy,
			pagination.sortOrder,
		],
		queryFn: async () => {
			console.log(
				'🔍 [KNOWLEDGE-QUERY] useKnowledgeQuery - Making API call with:',
				{
					selectedId,
					pagination,
				}
			);

			const data = await apis.getKnowledgeBase(selectedId, {
				page: pagination.currentPage,
				limit: pagination.pageSize,
				sortBy: pagination.sortBy,
				sortOrder: pagination.sortOrder,
			});

			console.log(
				'✅ [KNOWLEDGE-QUERY] useKnowledgeQuery - API response:',
				{
					status: data.status,
					dataKeys: Object.keys(data.data || {}),
					internal: data.data?.internal?.length || 0,
					external: data.data?.external?.length || 0,
					fullData: data.data,
				}
			);

			// Log sample articles if available
			if (data.data?.internal?.length > 0) {
				console.log(
					'📄 [KNOWLEDGE-QUERY] Sample internal articles:',
					data.data.internal.slice(0, 2).map((a: any) => ({
						_id: a._id,
						title: a.title,
						status: a.status,
						scrapingStatus: a.scrapingStatus,
					}))
				);
			}

			return data.data;
		},
		enabled: enabled && !!selectedId,
		retry: 0,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

/**
 * Custom hook for fetching paginated internal knowledge data
 * @param options - Query options including pagination state
 * @returns React Query result with internal knowledge data and pagination info
 */
export const useInternalKnowledgeQuery = (
	options: UseKnowledgeQueryOptions = {}
) => {
	const {
		selectedId,
		pagination = {
			currentPage: 1,
			pageSize: 10,
			sortBy: 'createdAt',
			sortOrder: 'desc',
		},
		enabled = true,
	} = options;

	return useQuery({
		queryKey: [
			'internal-knowledge',
			selectedId,
			pagination.currentPage,
			pagination.pageSize,
			pagination.sortBy,
			pagination.sortOrder,
		],
		queryFn: async () => {
			const data = await apis.getInternalKnowledgePaginated(selectedId, {
				page: pagination.currentPage,
				limit: pagination.pageSize,
				sortBy: pagination.sortBy,
				sortOrder: pagination.sortOrder,
			});
			return data.data;
		},
		enabled: enabled && !!selectedId,
		retry: 0,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

/**
 * Utility function to create pagination state
 * @param initialState - Initial pagination state
 * @returns Pagination state object
 */
export const createPaginationState = (
	initialState: Partial<KnowledgePaginationState> = {}
): KnowledgePaginationState => ({
	currentPage: 1,
	pageSize: 10,
	sortBy: 'createdAt',
	sortOrder: 'desc',
	...initialState,
});

/**
 * Utility function to update pagination state
 * @param currentState - Current pagination state
 * @param updates - Updates to apply
 * @returns Updated pagination state
 */
export const updatePaginationState = (
	currentState: KnowledgePaginationState,
	updates: Partial<KnowledgePaginationState>
): KnowledgePaginationState => ({
	...currentState,
	...updates,
});

/**
 * Utility function to reset pagination to first page
 * @param currentState - Current pagination state
 * @returns Updated pagination state with page reset to 1
 */
export const resetPaginationPage = (
	currentState: KnowledgePaginationState
): KnowledgePaginationState => ({
	...currentState,
	currentPage: 1,
});
